/**
 * Singleton Web Speech API manager.
 * One active field at a time; all mic UIs sync via subscribe().
 */

const getSpeechRecognitionCtor = () => {
  if (typeof window === 'undefined') return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
};

/** Spoken punctuation → real symbols (Greek + common latin spellings). */
const SPOKEN_PUNCT = new Map([
  ['κόμμα', ','],
  ['κομμα', ','],
  ['comma', ','],
  ['τελεία', '.'],
  ['τελεια', '.'],
  ['teleia', '.'],
  ['period', '.'],
  ['θαυμαστικό', '!'],
  ['θαυμαστικο', '!'],
  ['thavmastiko', '!'],
  ['thaumastiko', '!'],
  ['ερωτηματικό', ';'],
  ['ερωτηματικο', ';'],
  ['erotimatiko', ';'],
  ['erwtimatiko', ';'],
  ['erwtimtiko', ';'],
]);

const normalizeTokenKey = (token) =>
  token
    .normalize('NFC')
    .toLocaleLowerCase('el-GR')
    .replace(/^['"«»]+|['"«»]+$/g, '');

/**
 * Replace spoken punctuation words with symbols and tidy spacing.
 */
export const normalizeSpokenPunctuation = (raw) => {
  if (!raw) return '';

  // Multi-word phrases first
  let text = raw
    .replace(/\bquestion\s+mark\b/gi, ';')
    .replace(/\bfull\s+stop\b/gi, '.')
    .replace(/\bexclamation\s+mark\b/gi, '!');

  text = text.replace(/\S+/g, (token) => {
    const key = normalizeTokenKey(token);
    if (SPOKEN_PUNCT.has(key)) return SPOKEN_PUNCT.get(key);

    // "τελεία." style leftovers
    const stripped = key.replace(/[.,!?;:]+$/g, '');
    if (SPOKEN_PUNCT.has(stripped)) return SPOKEN_PUNCT.get(stripped);

    return token;
  });

  return text
    .replace(/\s+([.,!?;:])/g, '$1')
    .replace(/([.,!?;:])(?=[^\s.,!?;:])/g, '$1 ')
    .replace(/\s{2,}/g, ' ')
    .replace(/^\s+/, '');
};

/** Capitalize first letter of the string and after . ! ; (Greek ;) */
export const applySentenceCapitalization = (text) => {
  if (!text) return text;
  return text.replace(/(^|[.!;]\s+)(\p{L})/gu, (_, boundary, letter) => {
    return boundary + letter.toLocaleUpperCase('el-GR');
  });
};

const formatSpeechChunk = (chunk) => normalizeSpokenPunctuation(chunk || '');

const joinTranscript = (base, addition) => {
  const a = (base || '').replace(/\s+$/, '');
  let b = (addition || '').replace(/^\s+/, '');
  if (!b) return base || '';

  // After a sentence end in the base, force capital on the addition
  if (a && /[.!;]$/.test(a)) {
    b = b.replace(/^(\p{L})/u, (ch) => ch.toLocaleUpperCase('el-GR'));
  }

  if (!a) return b;
  if (/^[.,;:!?;…)»\]%]/.test(b)) return `${a}${b}`;
  return `${a} ${b}`;
};

class SpeechRecognitionManager {
  constructor() {
    this.activeId = null;
    this.shouldListen = false;
    this.callbacks = null;
    this.recognition = null;
    this.sessionBase = '';
    this.committedExtra = '';
    this._outsideHandler = null;
    this._submitHandler = null;
    this._mediaStream = null;
    this._starting = false;
    this._uiListeners = new Set();
  }

  isSupported() {
    return Boolean(getSpeechRecognitionCtor());
  }

  isActive(id) {
    return this.shouldListen && this.activeId === id;
  }

  getActiveId() {
    return this.shouldListen ? this.activeId : null;
  }

  /** All mic buttons subscribe so only the active one stays red. */
  subscribe(listener) {
    this._uiListeners.add(listener);
    listener(this.getActiveId());
    return () => this._uiListeners.delete(listener);
  }

  _emitActiveChange() {
    const active = this.getActiveId();
    this._uiListeners.forEach((fn) => {
      try {
        fn(active);
      } catch (_) {
        /* ignore */
      }
    });
  }

  /**
   * @param {string} id
   * @param {{
   *   getValue: () => string,
   *   onValue: (value: string) => void,
   *   onError?: (message: string) => void,
   *   lang?: string,
   *   micEl?: Element | null,
   * }} options
   */
  async start(id, options) {
    const Ctor = getSpeechRecognitionCtor();
    if (!Ctor) {
      options.onError?.('Η αναγνώριση ομιλίας δεν υποστηρίζεται. Χρησιμοποιήστε Chrome ή Edge.');
      return;
    }

    // Toggle off if same mic is already active
    if (this.shouldListen && this.activeId === id) {
      this.stopAll();
      return;
    }

    // Stop previous field (clears its red state via emit)
    if (this.shouldListen && this.activeId !== id) {
      this._teardown({ keepMic: true });
    }

    this._starting = true;
    this.activeId = id;
    this.shouldListen = true;
    this.callbacks = options;
    this.sessionBase = '';
    this.committedExtra = '';
    this._emitActiveChange();
    this._bindOutsideStop(options.micEl || null);

    try {
      await this._ensureMicPermission();
    } catch (err) {
      this._starting = false;
      options.onError?.(
        'Δεν δόθηκε άδεια μικροφώνου. Επιτρέψτε το μικρόφωνο για localhost και ξαναδοκιμάστε.'
      );
      this._teardown();
      return;
    }

    if (!this.shouldListen || this.activeId !== id) {
      this._starting = false;
      return;
    }

    // Snapshot AFTER mic unlock so we always append to the latest field text
    this.sessionBase = options.getValue?.() ?? '';
    this.committedExtra = '';

    this._ensureRecognition(Ctor, options.lang || 'el-GR');
    this._safeStart();
    this._starting = false;
  }

  stop(id) {
    if (id != null && this.activeId !== id) return;
    this._teardown();
  }

  stopAll() {
    this._teardown();
  }

  async _ensureMicPermission() {
    if (!navigator.mediaDevices?.getUserMedia) return;
    if (this._mediaStream) {
      const live = this._mediaStream.getTracks().some((t) => t.readyState === 'live');
      if (live) return;
      this._releaseMic();
    }
    this._mediaStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
      },
    });
  }

  _releaseMic() {
    if (this._mediaStream) {
      this._mediaStream.getTracks().forEach((t) => t.stop());
      this._mediaStream = null;
    }
  }

  _ensureRecognition(Ctor, lang) {
    if (this.recognition) {
      const old = this.recognition;
      this.recognition = null;
      try {
        old.onresult = null;
        old.onerror = null;
        old.onend = null;
        old.abort();
      } catch (_) {
        /* ignore */
      }
    }

    const recognition = new Ctor();
    recognition.lang = lang;
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    recognition.onresult = (event) => {
      if (!this.shouldListen || !this.callbacks) return;

      let interim = '';
      let finals = '';

      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        const text = result[0]?.transcript || '';
        if (!text) continue;
        if (result.isFinal) finals += text;
        else interim += text;
      }

      if (finals) {
        this.committedExtra = joinTranscript(
          this.committedExtra,
          formatSpeechChunk(finals)
        );
      }

      const merged = joinTranscript(
        joinTranscript(this.sessionBase, this.committedExtra),
        formatSpeechChunk(interim)
      );
      const next = applySentenceCapitalization(merged);

      try {
        this.callbacks.onValue?.(next);
      } catch (err) {
        console.error('Speech onValue failed:', err);
      }
    };

    recognition.onerror = (event) => {
      if (event.error === 'aborted') return;
      if (event.error === 'no-speech') return;
      if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
        this.callbacks?.onError?.(
          'Δεν δόθηκε άδεια μικροφώνου. Επιτρέψτε το μικρόφωνο στον browser.'
        );
        this._teardown();
        return;
      }
      if (event.error === 'network') {
        this.callbacks?.onError?.(
          'Η αναγνώριση ομιλίας χρειάζεται σύνδεση στο internet (Chrome). Ελέγξτε το δίκτυο.'
        );
        this._teardown();
        return;
      }
      console.warn('SpeechRecognition error:', event.error);
    };

    recognition.onend = () => {
      if (this.shouldListen && this.recognition === recognition && !this._starting) {
        window.setTimeout(() => {
          if (this.shouldListen && this.recognition === recognition) {
            this._safeStart();
          }
        }, 80);
      }
    };

    this.recognition = recognition;
  }

  _safeStart() {
    if (!this.recognition || !this.shouldListen) return;
    try {
      this.recognition.start();
    } catch (err) {
      if (err?.name !== 'InvalidStateError') {
        console.warn('SpeechRecognition start failed:', err);
      }
    }
  }

  _bindOutsideStop(micEl) {
    this._unbindOutsideStop();

    const openedAt = Date.now();

    this._outsideHandler = (event) => {
      if (!this.shouldListen) return;
      if (Date.now() - openedAt < 300) return;

      const target = event.target;
      if (!(target instanceof Element)) return;

      if (micEl && (micEl === target || micEl.contains(target))) return;
      if (target.closest?.('[data-voice-mic="true"]')) return;

      const interactive = target.closest?.(
        'input, textarea, select, button, a, [contenteditable="true"], .ql-editor, [role="button"], [type="submit"]'
      );
      if (interactive) {
        this.stopAll();
      }
    };

    this._submitHandler = () => {
      if (this.shouldListen) this.stopAll();
    };

    document.addEventListener('pointerdown', this._outsideHandler, true);
    document.addEventListener('submit', this._submitHandler, true);
  }

  _unbindOutsideStop() {
    if (this._outsideHandler) {
      document.removeEventListener('pointerdown', this._outsideHandler, true);
      this._outsideHandler = null;
    }
    if (this._submitHandler) {
      document.removeEventListener('submit', this._submitHandler, true);
      this._submitHandler = null;
    }
  }

  _teardown({ keepMic = false } = {}) {
    this.shouldListen = false;
    this._starting = false;
    this.activeId = null;
    this.callbacks = null;
    this.sessionBase = '';
    this.committedExtra = '';
    this._unbindOutsideStop();
    if (!keepMic) this._releaseMic();

    if (this.recognition) {
      const rec = this.recognition;
      this.recognition = null;
      try {
        rec.onresult = null;
        rec.onerror = null;
        rec.onend = null;
        rec.abort();
      } catch (_) {
        try {
          rec.stop();
        } catch (__) {
          /* ignore */
        }
      }
    }

    this._emitActiveChange();
  }
}

const speechRecognitionManager = new SpeechRecognitionManager();

export default speechRecognitionManager;
export { joinTranscript };
